/**
 * GuidedTour — orchestrator for the spotlight + tooltip experience.
 *
 * Responsibilities:
 *   - Resolve the target element for the current step from `targetSelector`.
 *   - Measure the target's bounding rect, refresh on resize/scroll.
 *   - Render `<TourSpotlight>` (cut-out overlay) + `<TourTooltip>` (card).
 *   - Wire keyboard navigation: ← regress, → / Space / Enter advance,
 *     Esc dismiss. Esc handling is delegated to Radix Dialog; the rest is
 *     a window keydown listener on the dialog root.
 *   - Use Radix Dialog under the hood for the focus trap, portal, and
 *     `aria-modal` semantics. The Dialog is hidden visually — we render
 *     our own surfaces — but its accessibility plumbing is what carries
 *     the experience.
 *
 * Props are controlled: the caller decides when `active` flips on. The
 * orchestrator never persists state itself — that's `useTour`'s job.
 *
 * Reduced-motion: detected once on mount + on `change` events from the
 * matchMedia query. Both the spotlight cut-out animation and the tooltip
 * entrance class flip off when set.
 *
 * Hand-off note (Stream C): this component is allowed to render zero DOM
 * when `active=false` — it short-circuits early, doesn't even mount Radix.
 * That keeps the Home page tree lightweight when the tour is inactive.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import TourSpotlight from './TourSpotlight';
import TourTooltip from './TourTooltip';
import type { TourRect, TourStep } from './types';
import './tour.css';

export interface GuidedTourProps {
  readonly tourId: string;
  readonly steps: ReadonlyArray<TourStep>;
  /** Controlled — caller (Stream C) decides when to start the tour. */
  readonly active: boolean;
  readonly onComplete?: () => void;
  readonly onSkip?: () => void;
}

const DEFAULT_PADDING = 8;

function readReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function rectFromElement(el: Element): TourRect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export default function GuidedTour({
  tourId,
  steps,
  active,
  onComplete,
  onSkip,
}: GuidedTourProps): ReactNode {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rect, setRect] = useState<TourRect | null>(null);
  const [reducedMotion, setReducedMotion] = useState<boolean>(() =>
    readReducedMotion(),
  );

  // Stable ARIA ids so the (visually hidden) Radix Dialog title/description
  // and our own tooltip can share them. Also lets screen readers announce
  // the heading + body on step change.
  const baseId = useId().replace(/[:]/g, '');
  const titleId = `wayne-tour-${baseId}-title`;
  const descriptionId = `wayne-tour-${baseId}-description`;

  // Reset to step 0 on each (re-)activation so re-running the tour from the
  // outside (Stream C clears storage + re-mounts) always begins at the top.
  useEffect(() => {
    if (active) {
      setCurrentIndex(0);
    }
  }, [active]);

  // Track reduced-motion changes live so a user toggling the OS setting
  // mid-tour gets the updated experience without a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent): void => {
      setReducedMotion(e.matches);
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    return undefined;
  }, []);

  const currentStep = active ? steps[currentIndex] : undefined;

  // Re-measure the target on step change AND on viewport changes. We poll
  // briefly on activation because the target may not be in the DOM yet
  // (e.g. the Home page just mounted). 10 attempts * 50ms is enough for
  // any realistic mount; if the target never appears we surface no rect
  // and the tooltip falls back to a centered position.
  const stepRef = useRef(currentStep);
  stepRef.current = currentStep;

  useEffect(() => {
    if (!active || !currentStep) {
      setRect(null);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const measure = (): void => {
      if (cancelled) return;
      const el = document.querySelector(currentStep.targetSelector);
      if (el) {
        setRect(rectFromElement(el));
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        pollTimer = setTimeout(measure, 50);
      } else {
        // Target never showed up — clear so the tooltip centers itself.
        setRect(null);
      }
    };

    measure();

    const onWindowChange = (): void => {
      const el = document.querySelector(currentStep.targetSelector);
      if (el) setRect(rectFromElement(el));
    };
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onWindowChange, true);
    };
  }, [active, currentStep]);

  const totalSteps = steps.length;
  const isLastStep = currentIndex >= totalSteps - 1;

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete?.();
      return;
    }
    setCurrentIndex((i) => Math.min(i + 1, totalSteps - 1));
  }, [isLastStep, onComplete, totalSteps]);

  const handleBack = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  // Radix Dialog also fires `onOpenChange(false)` on Esc, and our window
  // keydown listener fires `handleSkip` directly. Guard against the double
  // invocation so callers' `onSkip` is called exactly once per dismissal.
  const skippedRef = useRef(false);
  useEffect(() => {
    if (active) skippedRef.current = false;
  }, [active]);

  const handleSkip = useCallback(() => {
    if (skippedRef.current) return;
    skippedRef.current = true;
    onSkip?.();
  }, [onSkip]);

  // Keyboard wiring: arrow keys + Space/Enter advance, Esc dismisses. Radix
  // Dialog also handles Esc via `onOpenChange(false)`, but we wire it here
  // too so the contract holds in environments (jsdom) where Radix's
  // document-level listener doesn't reliably catch synthesized events.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      // Don't hijack typing in form fields if the page underneath happens to
      // have one focused (rare during a tour, but defensible).
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleBack();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, handleNext, handleBack, handleSkip]);

  const eyebrow = useMemo(
    () => `Tour · Step ${currentIndex + 1} of ${totalSteps}`,
    [currentIndex, totalSteps],
  );

  if (!active || !currentStep || totalSteps === 0) {
    return null;
  }

  const padding = currentStep.padding ?? DEFAULT_PADDING;
  const placement = currentStep.placement ?? 'auto';

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        // Esc / outside-click → treat as Skip per the UX spec.
        if (!open) handleSkip();
      }}
    >
      <Dialog.Portal>
        {/* The Dialog Overlay is intentionally NOT visible — our own
            <TourSpotlight> draws the dim layer with a cut-out. Keep the
            Radix overlay element so its `aria-modal` semantics + scroll
            lock + Esc handler stay wired. */}
        <Dialog.Overlay
          data-testid="tour-radix-overlay"
          className="fixed inset-0 z-[68] bg-transparent pointer-events-none"
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          data-testid="tour-root"
          data-tour-id={tourId}
          data-step-index={currentIndex}
          // Visually hide the Radix content — the actual tour surfaces are
          // siblings rendered below. Keep it focusable so the focus trap
          // works.
          className="fixed inset-0 z-[69] outline-none pointer-events-none"
          // Don't auto-focus the Dialog content; let our tooltip's "Next"
          // button receive focus instead (it has `autoFocus`).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            // Don't dismiss on outside pointer-down — user might be reading
            // the spotlit content. Only Esc + explicit Skip should close.
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          {/* SR-only title/description for screen readers; visual title
              lives in the tooltip itself with the same ids. */}
          <Dialog.Title className="sr-only" id={titleId}>
            {currentStep.title}
          </Dialog.Title>
          <Dialog.Description className="sr-only" id={descriptionId}>
            {currentStep.body}
          </Dialog.Description>

          <div className="pointer-events-auto">
            <TourSpotlight
              rect={rect}
              padding={padding}
              reducedMotion={reducedMotion}
            />
            <TourTooltip
              eyebrow={eyebrow}
              title={currentStep.title}
              body={currentStep.body}
              currentStep={currentIndex}
              totalSteps={totalSteps}
              isLastStep={isLastStep}
              canGoBack={currentIndex > 0}
              placement={placement}
              targetRect={rect}
              reducedMotion={reducedMotion}
              onSkip={handleSkip}
              onBack={handleBack}
              onNext={handleNext}
              titleId={`${titleId}-visual`}
              descriptionId={`${descriptionId}-visual`}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
